import {
  mergeAuditTrailRowVersionContentDeep,
  mergeAuditVersionContent,
} from '../../AuditTrail/utils/auditTrailProcedureIdentity';

function sortBySibling(arr) {
  if (!Array.isArray(arr)) return [];
  return [...arr].sort((a, b) => {
    const aO = parseInt(
      a?.sibling_order ?? a?.procedure_tree?.sibling_order ?? '0',
      10,
    );
    const bO = parseInt(
      b?.sibling_order ?? b?.procedure_tree?.sibling_order ?? '0',
      10,
    );
    return aO - bO;
  });
}

function rowFromNode(node, typeHint) {
  if (!node || typeof node !== 'object') return null;
  const n = JSON.parse(JSON.stringify(node));
  delete n.steps;
  delete n.substeps;
  delete n.children;
  if (typeHint) n.type = n.type || typeHint;
  return n;
}

function walkChild(child, rows) {
  if (!child) return;
  const row = rowFromNode(child, child.type || null);
  if (row) rows.push(row);
}

function walkSubstep(ss, rows) {
  if (!ss) return;
  const row = rowFromNode(ss, 'substep');
  if (row) rows.push(row);
  sortBySibling(ss.children || []).forEach((c) => walkChild(c, rows));
}

function walkStep(st, rows) {
  if (!st) return;
  const row = rowFromNode(st, 'step');
  if (row) rows.push(row);
  sortBySibling(st.children || []).forEach((c) => walkChild(c, rows));
  sortBySibling(st.substeps || []).forEach((ss) => walkSubstep(ss, rows));
}

function walkSection(sec, rows) {
  if (!sec) return;
  const row = rowFromNode(sec, 'section');
  if (row) rows.push(row);
  sortBySibling(sec.children || []).forEach((c) => walkChild(c, rows));
  sortBySibling(sec.steps || []).forEach((st) => walkStep(st, rows));
}

/**
 * Flatten nested getRevAllNodes-style subtree into a list suitable for `procedure_node_data`.
 */
export function buildProcedureNodeDataFromNestedSource(root, sourceNodeType) {
  const rows = [];
  if (!root) return rows;
  if (sourceChildNodeType(sourceNodeType)) {
    walkChild(root, rows);
    return rows;
  }
  if (sourceNodeType === 'section') {
    walkSection(root, rows);
  } else if (sourceNodeType === 'step') {
    walkStep(root, rows);
  } else if (sourceNodeType === 'substep') {
    walkSubstep(root, rows);
  }
  return rows;
}

function sourceChildNodeType(nodeType) {
  return ['attachment', 'video', 'rich-text', 'form', 'canva', 'canvas'].includes(
    nodeType,
  );
}

function extractProcedureNodeDataFromAudit(auditTrailRow) {
  if (!auditTrailRow) return null;
  const vc =
    mergeAuditTrailRowVersionContentDeep(auditTrailRow) ||
    mergeAuditVersionContent(auditTrailRow) ||
    auditTrailRow.version_content ||
    {};
  if (Array.isArray(vc.procedure_node_data) && vc.procedure_node_data.length > 0) {
    return vc.procedure_node_data;
  }
  const nc = vc.newContent;
  if (
    nc &&
    typeof nc === 'object' &&
    !Array.isArray(nc) &&
    Array.isArray(nc.procedure_node_data) &&
    nc.procedure_node_data.length > 0
  ) {
    return nc.procedure_node_data;
  }
  return null;
}

/**
 * Prefer audit-stored `procedure_node_data`; otherwise flatten the source subtree from live nodes.
 */
export function resolveProcedureNodeDataForVersionHistoryCopy({
  auditTrailRow,
  sourceSubtreeRoot,
  sourceNodeType,
  sourceChildNode,
}) {
  const fromAudit = extractProcedureNodeDataFromAudit(auditTrailRow);
  if (Array.isArray(fromAudit) && fromAudit.length > 0) {
    return fromAudit;
  }
  if (sourceChildNode) {
    return buildProcedureNodeDataFromNestedSource(sourceChildNode, sourceNodeType);
  }
  if (
    sourceSubtreeRoot &&
    ['section', 'step', 'substep'].includes(sourceNodeType)
  ) {
    return buildProcedureNodeDataFromNestedSource(
      sourceSubtreeRoot,
      sourceNodeType,
    );
  }
  return [];
}
